import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, CalendarClock, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { createOrder } from "../lib/api";
import { useCartStore, calculateAge } from "../store/cartStore";
import { useAuthStore } from "../store/authStore";
import CheckoutFormules from "../components/CheckoutFormules";

export default function Checkout() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  const { items, hasAgeRestrictedItems, clearCart } = useCartStore();
  const restricted = hasAgeRestrictedItems();
  const [quote, setQuote] = useState(null);

  const [address, setAddress] = useState(user?.address || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [npa, setNpa] = useState("");
  const [city, setCity] = useState("");
  const [bDay, setBDay] = useState("");
  const [bMonth, setBMonth] = useState("");
  const [bYear, setBYear] = useState("");
  const birthDate = bDay && bMonth && bYear ? "" + bYear + "-" + bMonth + "-" + bDay : "";
  const [busy, setBusy] = useState(false);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" style={{ color: "#64748b" }}>
        <p className="font-semibold mb-4">Votre panier est vide.</p>
        <button onClick={() => nav("/boutique")} className="font-bold" style={{ color: "#f97316" }}>← Aller à la boutique</button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!address.trim()) { toast.error("Adresse de livraison requise"); return; }
    if (!phone.trim()) { toast.error("Numéro de téléphone requis"); return; }
    if (restricted) {
      if (!birthDate) { toast.error("Date de naissance requise pour les produits soumis à l'âge"); return; }
      if (calculateAge(birthDate) < 18) { toast.error("Vous devez avoir au moins 18 ans pour ces produits"); return; }
    }
    setBusy(true);
    try {
      await createOrder({
        customer_address: address.trim(),
        customer_phone: phone.trim(),
        items: items.map((i) => ({ product_id: i.id, product_name: i.title, quantity: i.quantity, price: i.price, image_url: i.image_url || null })),
        delivery_location: { address: address.trim(), npa: npa.trim() || null, city: city.trim() || null },
        promo_code: quote?.promo_code || null,
      });
      clearCart();
      toast.success("Commande confirmée !");
      nav("/commandes");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Impossible de créer la commande");
    } finally { setBusy(false); }
  };

  const inputCls = "flex-1 bg-transparent outline-none text-[15px]";
  const boxCls = "flex items-center gap-3 px-4 rounded-xl py-3 mb-3 bg-white";
  const boxStyle = { border: "1px solid #e5e7eb" };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-extrabold mb-6" style={{ color: "#0b1220" }}>Finaliser ma commande</h1>
      <form onSubmit={submit}>
        <h2 className="text-xs font-extrabold tracking-widest mb-3" style={{ color: "#64748b" }}>LIVRAISON</h2>
        <div className={boxCls} style={boxStyle}>
          <MapPin size={20} color="#0b1220" />
          <input className={inputCls} placeholder="Adresse (rue et numéro) *" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="checkout-address" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className={boxCls} style={boxStyle}><input className={inputCls} placeholder="NPA" value={npa} onChange={(e) => setNpa(e.target.value)} data-testid="checkout-npa" /></div>
          <div className={boxCls} style={boxStyle}><input className={inputCls} placeholder="Localité" value={city} onChange={(e) => setCity(e.target.value)} data-testid="checkout-city" /></div>
        </div>
        <div className={boxCls} style={boxStyle}>
          <Phone size={20} color="#0b1220" />
          <input className={inputCls} placeholder="Téléphone *" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="checkout-phone" />
        </div>

        {restricted && (
          <div className="rounded-xl p-4 mb-3" style={{ background: "#fffbeb", border: "1px solid #fde68a" }} data-testid="checkout-age">
            <div className="flex items-center gap-2 mb-2" style={{ color: "#92400e" }}>
              <ShieldAlert size={18} /> <span className="text-sm font-bold">Vérification d’âge requise</span>
            </div>
            <p className="text-xs mb-3" style={{ color: "#92400e" }}>Votre panier contient des produits soumis à une limite d’âge (alcool/tabac).</p>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: "#92400e" }}>Date de naissance (JJ.MM.AAAA)</label>
            <div className="flex items-center gap-2" data-testid="checkout-birthdate">
              <CalendarClock size={20} color="#92400e" className="flex-shrink-0" />
              <select value={bDay} onChange={(e) => setBDay(e.target.value)} data-testid="checkout-bday"
                className="flex-1 bg-white rounded-xl px-3 py-3 text-[15px] outline-none" style={boxStyle}>
                <option value="">Jour</option>
                {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={bMonth} onChange={(e) => setBMonth(e.target.value)} data-testid="checkout-bmonth"
                className="flex-1 bg-white rounded-xl px-3 py-3 text-[15px] outline-none" style={boxStyle}>
                <option value="">Mois</option>
                {"Janvier,Février,Mars,Avril,Mai,Juin,Juillet,Août,Septembre,Octobre,Novembre,Décembre".split(",").map((name, i) => (
                  <option key={name} value={String(i + 1).padStart(2, "0")}>{name}</option>
                ))}
              </select>
              <select value={bYear} onChange={(e) => setBYear(e.target.value)} data-testid="checkout-byear"
                className="flex-1 bg-white rounded-xl px-3 py-3 text-[15px] outline-none" style={boxStyle}>
                <option value="">Année</option>
                {Array.from({ length: 90 }, (_, i) => new Date().getFullYear() - 16 - i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {birthDate && <p className="text-xs mt-2 font-semibold" style={{ color: "#92400e" }}>Sélectionné : {bDay}.{bMonth}.{bYear}</p>}
          </div>
        )}

        <div className="mt-4">
          <CheckoutFormules items={items} onQuote={setQuote} />
        </div>

        <button type="submit" disabled={busy} data-testid="checkout-submit"
          className="w-full rounded-xl font-bold text-white py-3.5 mt-5 flex items-center justify-center gap-2" style={{ background: "#16a34a" }}>
          <CheckCircle2 size={20} /> {busy ? "Traitement..." : "Confirmer ma commande"}
        </button>
      </form>
    </div>
  );
}
